import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { Link } from 'react-router-dom';
import { ASSISTANT_MEMORY_MAX_LENGTH } from '@familyhub/shared';
import type { AssistantMemory, AssistantPairingResponse, AssistantStatusResponse } from '@familyhub/shared';
import { addMemory, editMemory, forget, getAssistant, putAssistantSettings, requestPairingCode, unpair } from './api';
import { useRefreshOnReturn } from '../useRefreshOnReturn';
import { useSession } from '../session';

/**
 * `/huub` — the household's Signal assistant (docs/ASSISTENT.md §11).
 *
 * Four things, top to bottom: whether he is set up and connected (so "he doesn't
 * answer" has somewhere to be diagnosed), pairing, the switches, and his memory.
 * Online-only like the recipe settings; a module-level cache keeps the last
 * answer across navigation.
 */

let cached: AssistantStatusResponse | null = null;

const dateFmt = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' });
const timeFmt = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' });

function linkLine(s: AssistantStatusResponse): { text: string; problem: boolean } {
  if (!s.configured) return { text: `${s.name} is nog niet ingesteld op de server.`, problem: true };
  switch (s.link.state) {
    case 'connected':
      return s.model_configured
        ? { text: `${s.name} is verbonden met Signal.`, problem: false }
        : { text: `${s.name} is verbonden en herinnert, maar zijn model is niet ingesteld — praten lukt nog niet.`, problem: true };
    case 'never':
      return { text: `${s.name} is ingesteld maar heeft Signal nog nooit bereikt.`, problem: true };
    default:
      return {
        text: `${s.name} heeft sinds ${s.link.heartbeat_at ? timeFmt.format(new Date(s.link.heartbeat_at)) : '—'} niets van Signal gehoord.${
          s.link.last_error ? ` (${s.link.last_error})` : ''
        }`,
        problem: true,
      };
  }
}

export function HuubScreen() {
  const session = useSession();
  const me = session.status === 'in' ? session.me?.user.id : undefined;
  const [data, setData] = useState<AssistantStatusResponse | null>(cached);
  const [failed, setFailed] = useState(false);
  const [code, setCode] = useState<AssistantPairingResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (res: AssistantStatusResponse) => {
    cached = res;
    setData(res);
    setFailed(false);
  };

  const refresh = useCallback(async () => {
    try {
      apply(await getAssistant());
    } catch {
      if (!cached) setFailed(true);
    }
  }, []);
  useRefreshOnReturn(refresh);
  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** True once the change landed; a refusal the server explains is shown. */
  async function run(fn: () => Promise<AssistantStatusResponse>): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      apply(await fn());
      return true;
    } catch (err) {
      // A failed change leaves the screen as it was; the next refresh is the truth.
      setError(err instanceof ApiError && err.status === 400 ? err.message : 'Dat lukte niet — probeer het zo nog eens.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const memoryActions = {
    busy,
    onSave: (id: string, text: string) => run(() => editMemory(id, { text })),
    onMove: (id: string, kind: 'fact' | 'persona') => run(() => editMemory(id, { kind })),
    onForget: (id: string) => run(() => forget(id)),
  };

  async function pair() {
    setBusy(true);
    try {
      setCode(await requestPairingCode());
    } catch {
      setCode(null);
    } finally {
      setBusy(false);
    }
  }

  const name = data?.name ?? 'Huub';
  const mine = data?.members.find((m) => m.user_id === me);
  // His character (§8.1) is shaped separately from what he knows; oldest first,
  // the order he reads it in.
  const character = (data?.memories ?? []).filter((m) => m.kind === 'persona').reverse();
  const facts = (data?.memories ?? []).filter((m) => m.kind !== 'persona');

  return (
    <div className="app">
      <div className="topbar">
        <Link className="btn btn-quiet back" to="/" aria-label="Naar het overzicht">
          ‹ Hub
        </Link>
        <h1>{name}</h1>
        <span style={{ width: 52 }} aria-hidden />
      </div>

      <main className="stack">
        {data === null ? (
          <p className="row-sub">{failed ? `${name} is nu niet bereikbaar — probeer het zo nog eens.` : 'Laden…'}</p>
        ) : (
          <>
            <p className={linkLine(data).problem ? 'banner' : 'row-sub wraps'} role="status">
              {linkLine(data).text}
            </p>

            <p className="section-label">Koppelen</p>
            <div className="card">
              {data.members.map((m) => (
                <div className="row" key={m.user_id}>
                  <span className="row-main">
                    <span className="row-name">{m.display_name}</span>
                    <span className="row-sub">
                      {m.paired && m.paired_at ? `Gekoppeld sinds ${dateFmt.format(new Date(m.paired_at))}` : 'Niet gekoppeld'}
                    </span>
                  </span>
                  {m.paired && (
                    <button
                      className="btn btn-quiet"
                      disabled={busy}
                      aria-label={`Ontkoppel ${m.display_name}`}
                      onClick={() => void run(() => unpair(m.user_id))}
                    >
                      Ontkoppel
                    </button>
                  )}
                </div>
              ))}
            </div>
            {code ? (
              <div className="card huub-code">
                <p className="row-sub wraps">Stuur dit in de Signal-groep{data.group_bound ? '' : ' waar ' + name + ' in moet wonen'}:</p>
                <p className="huub-code-text" aria-label={`Koppelbericht: @${name} koppel ${code.code}`}>
                  @{name} koppel {code.code}
                </p>
                <p className="row-sub">Geldig tot {timeFmt.format(new Date(code.expires_at))}.</p>
              </div>
            ) : (
              <button className="btn btn-primary" disabled={busy || !data.configured} onClick={() => void pair()}>
                {mine?.paired ? 'Opnieuw koppelen' : 'Koppel Signal'}
              </button>
            )}

            <p className="section-label">Instellingen</p>
            <div className="card">
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Leest altijd mee</span>
                  <span className="row-sub wraps">
                    Aan: {name} reageert op alles in de groep — en alles wat jullie daar zeggen gaat naar zijn
                    model (Scaleway, EU). Uit: alleen als je hem roept.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.always_respond}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ always_respond: e.target.checked }))}
                />
              </label>
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Ochtendbericht</span>
                  <span className="row-sub">Agenda, taken en regen voor vandaag.</span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.briefing_enabled}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ briefing_enabled: e.target.checked }))}
                />
              </label>
              {data.settings.briefing_enabled && (
                <label className="row">
                  <span className="row-main">
                    <span className="row-name">Om</span>
                  </span>
                  <input
                    type="time"
                    aria-label="Tijd van het ochtendbericht"
                    value={data.settings.briefing_time}
                    disabled={busy}
                    onChange={(e) => {
                      if (/^\d\d:\d\d$/.test(e.target.value)) void run(() => putAssistantSettings({ briefing_time: e.target.value }));
                    }}
                  />
                </label>
              )}
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Afvalherinnering</span>
                  <span className="row-sub wraps">
                    De avond voor Circulus komt: wat er aan de straat moet. Elk uur tot 22:00, tot iemand 👍 geeft.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.afval_enabled}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ afval_enabled: e.target.checked }))}
                />
              </label>
              <label className="row">
                <span className="row-main">
                  <span className="row-name">Het Woord</span>
                  <span className="row-sub wraps">
                    ’s Avonds een por voor wie het woord nog niet heeft gespeeld, als de ander al klaar is.
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={data.settings.wordle_nudge_enabled}
                  disabled={busy}
                  onChange={(e) => void run(() => putAssistantSettings({ wordle_nudge_enabled: e.target.checked }))}
                />
              </label>
              {data.settings.wordle_nudge_enabled && (
                <label className="row">
                  <span className="row-main">
                    <span className="row-name">Om</span>
                  </span>
                  <input
                    type="time"
                    aria-label="Tijd van de Het Woord-herinnering"
                    value={data.settings.wordle_nudge_time}
                    disabled={busy}
                    onChange={(e) => {
                      if (/^\d\d:\d\d$/.test(e.target.value)) void run(() => putAssistantSettings({ wordle_nudge_time: e.target.value }));
                    }}
                  />
                </label>
              )}
            </div>

            {error && <p className="banner">{error}</p>}

            <p className="section-label">Karakter</p>
            {character.length === 0 && (
              <p className="row-sub wraps">
                Nog niets. Zeg in de groep hoe {name} moet zijn — “{name}, je mag wat grappiger en vernijniger” — of schrijf
                het hier; hij bouwt daarop voort.
              </p>
            )}
            <MemoryList
              memories={character}
              kind="persona"
              addLabel="Karakter toevoegen"
              onAdd={(text) => run(() => addMemory({ kind: 'persona', text }))}
              {...memoryActions}
            />

            <p className="section-label">Geheugen</p>
            {data.memories.length >= data.memory_cap && (
              <p className="banner">Het geheugen is vol ({data.memory_cap}). Gooi iets weg, anders onthoudt {name} niets nieuws.</p>
            )}
            {facts.length === 0 && (
              <p className="row-sub wraps">
                Nog niets. Zeg “{name}, onthoud dat…” in de groep. Wachtwoorden, pincodes en BSN onthoudt hij nooit.
              </p>
            )}
            <MemoryList
              memories={facts}
              kind="fact"
              addLabel="Feit toevoegen"
              onAdd={(text) => run(() => addMemory({ kind: 'fact', text }))}
              {...memoryActions}
            />
          </>
        )}
      </main>
    </div>
  );
}

interface MemoryListProps {
  memories: AssistantMemory[];
  kind: 'fact' | 'persona';
  addLabel: string;
  busy: boolean;
  onAdd: (text: string) => Promise<boolean>;
  onSave: (id: string, text: string) => Promise<boolean>;
  /** To the other section: a fact that is really about his manner, or back. */
  onMove: (id: string, kind: 'fact' | 'persona') => Promise<boolean>;
  onForget: (id: string) => Promise<boolean>;
}

/** One section's rows; a row opens in place to edit, move or forget it. */
function MemoryList({ memories, kind, addLabel, busy, onAdd, onSave, onMove, onForget }: MemoryListProps) {
  // The row being edited ('new' for the add field), and its draft text.
  const [open, setOpen] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const other = kind === 'persona' ? 'fact' : 'persona';
  const close = () => {
    setOpen(null);
    setDraft('');
  };
  const done = async (p: Promise<boolean>) => {
    if (await p) close();
  };

  const editor = (label: string, onSubmit: () => void, extra?: React.ReactNode) => (
    <div className="memory-edit">
      <textarea
        rows={4}
        value={draft}
        aria-label={label}
        maxLength={ASSISTANT_MEMORY_MAX_LENGTH}
        autoFocus
        onChange={(e) => setDraft(e.target.value)}
      />
      <div className="memory-edit-actions">
        <button className="btn" disabled={busy || !draft.trim()} onClick={onSubmit}>
          Bewaar
        </button>
        <button className="btn btn-quiet" disabled={busy} onClick={close}>
          Annuleer
        </button>
        {extra}
      </div>
    </div>
  );

  return (
    <>
      {memories.length > 0 && (
        <div className="card">
          {memories.map((m) =>
            open === m.id ? (
              <div className="row" key={m.id}>
                {editor(
                  `Bewerk: ${m.text}`,
                  () => void done(onSave(m.id, draft.trim())),
                  <>
                    <button className="btn btn-quiet" disabled={busy} onClick={() => void done(onMove(m.id, other))}>
                      {other === 'persona' ? 'Naar karakter' : 'Naar geheugen'}
                    </button>
                    <button className="btn btn-quiet" disabled={busy} onClick={() => void done(onForget(m.id))}>
                      Vergeet
                    </button>
                  </>,
                )}
              </div>
            ) : (
              <div className="row row-top" key={m.id}>
                <span className="row-main">
                  <span className="row-name wraps">{m.text}</span>
                  <span className="row-sub">
                    {m.source === 'told' ? 'Verteld' : m.kind === 'persona' ? 'Zelf bedacht' : 'Opgepikt'}
                    {m.source === 'told' && m.created_by_name ? ` door ${m.created_by_name}` : ''} ·{' '}
                    {dateFmt.format(new Date(m.created_at))}
                  </span>
                </span>
                <button
                  className="btn btn-quiet"
                  disabled={busy}
                  aria-label={`Bewerk: ${m.text}`}
                  onClick={() => {
                    setOpen(m.id);
                    setDraft(m.text);
                  }}
                >
                  Bewerk
                </button>
              </div>
            ),
          )}
        </div>
      )}
      {open === 'new' ? (
        <div className="card">
          <div className="row">{editor(addLabel, () => void done(onAdd(draft.trim())))}</div>
        </div>
      ) : (
        <button
          className="btn btn-quiet memory-add"
          disabled={busy}
          onClick={() => {
            setOpen('new');
            setDraft('');
          }}
        >
          + {addLabel}
        </button>
      )}
    </>
  );
}
